import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Tier, type FeatureDef, type Tenant } from '@/lib/api';
import { Card, CardBody, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea, Select } from '@/components/ui/input';
import { Badge, statusTone } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Plus, GripVertical, ChevronDown, Users } from 'lucide-react';

// Mirrors TIER_ID_RE in control-plane/src/routes/tiers.ts.
const TIER_SLUG_RE = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

type TiersResponse = { tiers: Tier[]; features: FeatureDef[] };
type PatchResult = { tier: Tier; repush: { pushed: number; failed: number } };
type PushLimitResult = {
  attempted: number;
  ok: number;
  failed: number;
  skipped: { tenant_id: string; reason: string }[];
  force: boolean;
  results: { tenant_id: string; ok: boolean; status: number; reason?: string }[];
};

export function TiersPage() {
  const qc = useQueryClient();
  const tiers = useQuery({
    queryKey: ['tiers'],
    queryFn: () => api.get<TiersResponse>('/api/tiers'),
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  // Drag-and-drop ordering: while a drag is in flight (and until the refetch
  // after the commit catches up) `localOrder` overrides the server order, so
  // the list follows the cursor live instead of snapping back.
  const [dragId, setDragId] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  const features = tiers.data?.features ?? [];
  const serverTiers = useMemo(() => tiers.data?.tiers ?? [], [tiers.data]);

  const ordered = useMemo(() => {
    if (!localOrder) return serverTiers;
    const byId = new Map(serverTiers.map((t) => [t.id, t]));
    const known = localOrder.filter((id) => byId.has(id)).map((id) => byId.get(id)!);
    const missing = serverTiers.filter((t) => !localOrder.includes(t.id));
    return [...known, ...missing];
  }, [serverTiers, localOrder]);

  // Drop the local override once the server order matches it.
  useEffect(() => {
    if (localOrder && serverTiers.map((t) => t.id).join('\n') === localOrder.join('\n')) {
      setLocalOrder(null);
    }
  }, [serverTiers, localOrder]);

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.post('/api/tiers/reorder', { ids }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['tiers'] }),
    onError: () => setLocalOrder(null),
  });

  const moveOver = (overId: string) => {
    if (!dragId || dragId === overId) return;
    const ids = ordered.map((t) => t.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0 || from === to) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    setLocalOrder(ids);
  };

  const endDrag = () => {
    if (dragId && localOrder && localOrder.join('\n') !== serverTiers.map((t) => t.id).join('\n')) {
      reorder.mutate(localOrder);
    }
    setDragId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tiers</h1>
          <p className="text-sm text-slate-500 mt-1">
            Commercial tiers and the features they unlock. Each tenant is on one tier; the control
            plane pushes the tier's features to that tenant's orchestrator, which enforces them.
            Click a tier to edit it; drag the handle to reorder.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="shrink-0">
          <Plus size={16} className="mr-1.5" />
          New tier
        </Button>
      </div>

      {tiers.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : !serverTiers.length ? (
        <div className="text-sm text-slate-500">No tiers yet.</div>
      ) : (
        <div className="space-y-4">
          {ordered.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              features={features}
              allTiers={serverTiers}
              dragging={dragId === tier.id}
              onDragStart={() => setDragId(tier.id)}
              onDragOverCard={() => moveOver(tier.id)}
              onDragEnd={endDrag}
            />
          ))}
        </div>
      )}

      <NewTierDialog open={dialogOpen} onOpenChange={setDialogOpen} features={features} />
    </div>
  );
}

// Usage-limit modes, in the order the select shows them. The two metered modes
// are the same machine on different units — one grant engine, one gate, one
// end-user UI — so the form differs only in the amount field's label and step.
const CREDIT_MODES = [
  { value: 'none', label: 'No usage limits' },
  { value: 'usd', label: 'Dollar credit' },
  { value: 'runtime', label: 'Runtime credit' },
] as const;
type CreditMode = (typeof CREDIT_MODES)[number]['value'];

const amountLabel = (mode: CreditMode) =>
  mode === 'runtime' ? 'Minutes per period' : 'Amount per period ($)';
const amountUnit = (mode: CreditMode) => (mode === 'runtime' ? 'minutes' : 'dollars');
// A pending edit that moves between the two METERED modes re-denominates the
// amount without touching it: "5" stops meaning $5 and starts meaning 5
// minutes. Saving pushes to every tenant on the tier and re-grants at once
// (a mode change is a terms change), so it has to be said out loud.
const unitChanged = (from: CreditMode, to: CreditMode) =>
  from !== to && from !== 'none' && to !== 'none';
const amountStep = (mode: CreditMode) => (mode === 'runtime' ? '1' : '0.01');
// Minutes are whole-ish; dollars are not. Keeps the field honest either way.
const parseAmount = (raw: string, mode: CreditMode) =>
  mode === 'runtime'
    ? Math.max(0, parseInt(raw, 10) || 0)
    : Math.max(0, parseFloat(raw) || 0);

function TierCard({
  tier,
  features,
  allTiers,
  dragging,
  onDragStart,
  onDragOverCard,
  onDragEnd,
}: {
  tier: Tier;
  features: FeatureDef[];
  allTiers: Tier[];
  dragging: boolean;
  onDragStart: () => void;
  onDragOverCard: () => void;
  onDragEnd: () => void;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  // The card is only draggable while the pointer went down on the grip handle,
  // so text selection / clicks elsewhere never start a drag.
  const [dragArmed, setDragArmed] = useState(false);
  const [slug, setSlug] = useState(tier.id);
  const [label, setLabel] = useState(tier.label);
  const [description, setDescription] = useState(tier.description ?? '');
  const [limit, setLimit] = useState(tier.default_activations_limit);
  const [usersLimit, setUsersLimit] = useState(tier.default_users_limit);
  const [blockedMessage, setBlockedMessage] = useState(tier.chat_blocked_message ?? '');
  const [creditMode, setCreditMode] = useState<CreditMode>(tier.credit_mode);
  const [allowance, setAllowance] = useState(() => allowanceNum(tier.credit_allowance));
  const [periodHours, setPeriodHours] = useState(tier.credit_period_hours);
  // Explicit, not derived from periodHours — see PeriodPicker.
  const [periodCustom, setPeriodCustom] = useState(() => isCustomPeriod(tier.credit_period_hours));
  const [resetOnFirstConnect, setResetOnFirstConnect] = useState(tier.credit_reset_on_first_connect === true);
  const [allowanceTasks, setAllowanceTasks] = useState(tier.allowance_tasks);
  const [draft, setDraft] = useState<Record<string, boolean>>(() => ({ ...tier.features }));
  const [error, setError] = useState<string | null>(null);
  const [repush, setRepush] = useState<{ pushed: number; failed: number } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [tenantsOpen, setTenantsOpen] = useState(false);
  const [forceOverride, setForceOverride] = useState(false);
  const [pushResult, setPushResult] = useState<PushLimitResult | null>(null);
  // The users push gets its OWN force + result state so its button status never
  // shares with the activation-limit push above it.
  const [usersForceOverride, setUsersForceOverride] = useState(false);
  const [usersPushResult, setUsersPushResult] = useState<PushLimitResult | null>(null);

  // Re-seed local edit state if the row changes underneath us (e.g. another
  // save, or a features re-push elsewhere).
  useEffect(() => {
    setSlug(tier.id);
    setLabel(tier.label);
    setDescription(tier.description ?? '');
    setLimit(tier.default_activations_limit);
    setUsersLimit(tier.default_users_limit);
    setBlockedMessage(tier.chat_blocked_message ?? '');
    setCreditMode(tier.credit_mode);
    setAllowance(allowanceNum(tier.credit_allowance));
    setPeriodHours(tier.credit_period_hours);
    setPeriodCustom(isCustomPeriod(tier.credit_period_hours));
    setResetOnFirstConnect(tier.credit_reset_on_first_connect === true);
    setAllowanceTasks(tier.allowance_tasks);
    setDraft({ ...tier.features });
    setError(null);
    setRepush(null);
    setForceOverride(false);
    setPushResult(null);
    setUsersForceOverride(false);
    setUsersPushResult(null);
  }, [tier.id, tier.label, tier.description, tier.default_activations_limit, tier.default_users_limit, tier.chat_blocked_message, tier.credit_mode, tier.credit_allowance, tier.credit_period_hours, tier.credit_reset_on_first_connect, tier.allowance_tasks, tier.features]);

  const fieldsDirty =
    label !== tier.label ||
    description !== (tier.description ?? '') ||
    limit !== tier.default_activations_limit ||
    usersLimit !== tier.default_users_limit ||
    blockedMessage !== (tier.chat_blocked_message ?? '') ||
    creditMode !== tier.credit_mode ||
    allowance !== allowanceNum(tier.credit_allowance) ||
    periodHours !== tier.credit_period_hours ||
    resetOnFirstConnect !== (tier.credit_reset_on_first_connect === true) ||
    allowanceTasks !== tier.allowance_tasks ||
    !sameFeatures(draft, tier.features);
  const dirty = slug !== tier.id || fieldsDirty;

  const save = useMutation({
    // PATCH the field edits first (against the current slug), then rename — so
    // a failed rename still leaves the field edits saved.
    mutationFn: async (): Promise<{ pushed: number; failed: number }> => {
      if (slug !== tier.id && !TIER_SLUG_RE.test(slug)) {
        throw new Error('Slug must be lowercase letters, digits and hyphens (3–32 chars).');
      }
      const repush = { pushed: 0, failed: 0 };
      if (fieldsDirty) {
        const r = await api.patch<PatchResult>(`/api/tiers/${encodeURIComponent(tier.id)}`, {
          label,
          description: description || null,
          default_activations_limit: limit,
          default_users_limit: usersLimit,
          chat_blocked_message: blockedMessage,
          credit_mode: creditMode,
          credit_allowance: allowance,
          credit_period_hours: periodHours,
          credit_reset_on_first_connect: resetOnFirstConnect,
          allowance_tasks: allowanceTasks,
          features: draft,
        });
        repush.pushed += r.repush.pushed;
        repush.failed += r.repush.failed;
      }
      if (slug !== tier.id) {
        const r = await api.post<PatchResult>(`/api/tiers/${encodeURIComponent(tier.id)}/rename`, {
          new_id: slug,
        });
        repush.pushed += r.repush.pushed;
        repush.failed += r.repush.failed;
      }
      return repush;
    },
    onSuccess: (repush) => {
      setRepush(repush.pushed || repush.failed ? repush : null);
      qc.invalidateQueries({ queryKey: ['tiers'] });
      // Tenant rows carry the tier slug — refresh them after a rename.
      qc.invalidateQueries({ queryKey: ['tenants'] });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const makeDefault = useMutation({
    mutationFn: () =>
      api.patch<PatchResult>(`/api/tiers/${encodeURIComponent(tier.id)}`, { is_default: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tiers'] }),
    onError: (err: Error) => setError(err.message),
  });

  const pushLimit = useMutation({
    mutationFn: (force: boolean) =>
      api.post<PushLimitResult>(`/api/tiers/${encodeURIComponent(tier.id)}/push-activation-limit`, { force }),
    onSuccess: (data) => {
      setPushResult(data);
      setError(null);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const pushUsersLimit = useMutation({
    mutationFn: (force: boolean) =>
      api.post<PushLimitResult>(`/api/tiers/${encodeURIComponent(tier.id)}/push-users-limit`, { force }),
    onSuccess: (data) => {
      setUsersPushResult(data);
      setError(null);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div
      draggable={dragArmed}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        // Firefox refuses to start a drag without payload data.
        e.dataTransfer.setData('text/plain', tier.id);
        onDragStart();
      }}
      onDragEnd={() => {
        setDragArmed(false);
        onDragEnd();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOverCard();
      }}
      onDrop={(e) => e.preventDefault()}
      onMouseUp={() => setDragArmed(false)}
      onMouseLeave={() => setDragArmed(false)}
    >
      <Card className={dragging ? 'opacity-50' : undefined}>
        <div
          className={cn(
            'px-5 py-4 cursor-pointer select-none rounded-lg hover:bg-slate-50',
            expanded && 'rounded-b-none border-b border-slate-200'
          )}
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                onMouseDown={() => setDragArmed(true)}
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 -ml-2 p-1 rounded text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
                title="Drag to reorder"
              >
                <GripVertical size={16} />
              </span>
              <div className="min-w-0">
                <CardTitle>
                  {tier.label}{' '}
                  <code className="font-mono text-xs font-normal text-slate-500">{tier.id}</code>
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {tier.tenant_count} tenant{tier.tenant_count === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setTenantsOpen(true);
                }}
                disabled={tier.tenant_count === 0}
                className="text-xs h-8"
                title={
                  tier.tenant_count === 0
                    ? 'No tenants on this tier.'
                    : 'List the tenants on this tier'
                }
              >
                <Users size={14} />
                View tenants
              </Button>
              {tier.is_default ? (
                <Badge tone="info">default</Badge>
              ) : (
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    makeDefault.mutate();
                  }}
                  disabled={makeDefault.isPending}
                  className="text-xs h-8"
                >
                  Make default
                </Button>
              )}
              <ChevronDown
                size={16}
                className={cn('text-slate-400 transition-transform', expanded && 'rotate-180')}
              />
            </div>
          </div>
        </div>
        {expanded && (
          <CardBody className="space-y-4">
            {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`slug-${tier.id}`}>Slug (ID)</Label>
                <Input id={`slug-${tier.id}`} value={slug} onChange={(e) => setSlug(e.target.value)} />
                <p className="mt-1 text-xs text-slate-500">
                  Lowercase + hyphens. Renaming moves the tier's tenants to the new slug and re-pushes
                  it to their orchestrators.
                </p>
              </div>
              <div>
                <Label htmlFor={`label-${tier.id}`}>Label</Label>
                <Input id={`label-${tier.id}`} value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
            </div>

            <div>
              <Label htmlFor={`desc-${tier.id}`}>Description</Label>
              <Input
                id={`desc-${tier.id}`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor={`limit-${tier.id}`}>Default activation limit</Label>
              <Input
                id={`limit-${tier.id}`}
                type="number"
                min={0}
                value={limit}
                onChange={(e) => setLimit(Math.max(0, parseInt(e.target.value, 10) || 0))}
                className="sm:max-w-[200px]"
              />
              <p className="mt-1 text-xs text-slate-500">
                Seeds the activation (site) cap for license keys on this tier (0 blocks all activations).
                Save applies it to <span className="font-medium">new</span> tenants; use “Push to tenants”
                to apply a change to existing tenants too. Tenants that set their own value are skipped
                unless you override. Per-license overrides are never touched.
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => pushLimit.mutate(forceOverride)}
                  disabled={pushLimit.isPending || limit !== tier.default_activations_limit}
                  title={
                    limit !== tier.default_activations_limit
                      ? 'Save the limit first, then push the saved value.'
                      : forceOverride
                        ? 'Push to every tenant on this tier, overwriting ones that customized their default in their own dashboard. Per-license overrides are untouched.'
                        : 'Push to tenants on this tier still on the value the CP last sent them. Tenants that customized their default are skipped; offline tenants come back as failures.'
                  }
                >
                  {pushLimit.isPending ? 'Pushing…' : forceOverride ? 'Force-push to all tenants' : 'Push to tenants'}
                </Button>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={forceOverride}
                    onChange={(e) => setForceOverride(e.target.checked)}
                    className="cursor-pointer"
                  />
                  Also overwrite customized tenants
                </label>
              </div>
              {pushResult && (
                <div className="mt-2 text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2">
                  Pushed to {pushResult.ok} tenant{pushResult.ok === 1 ? '' : 's'}
                  {pushResult.skipped.length > 0 && <>, skipped {pushResult.skipped.length} customized</>}
                  {pushResult.failed > 0 && <span className="text-red-600">, {pushResult.failed} failed</span>}.
                </div>
              )}
            </div>

            <div>
              <Label htmlFor={`users-limit-${tier.id}`}>Default user limit</Label>
              <Input
                id={`users-limit-${tier.id}`}
                type="number"
                min={1}
                value={usersLimit}
                onChange={(e) => setUsersLimit(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="sm:max-w-[200px]"
              />
              <p className="mt-1 text-xs text-slate-500">
                Seeds the user (seat) cap for license keys on this tier (minimum 1 — a site always has
                its owner). Save applies it to <span className="font-medium">new</span> tenants; use
                “Push to tenants” to apply a change to existing tenants too. Tenants that set their own
                value are skipped unless you override. Per-license overrides are never touched.
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => pushUsersLimit.mutate(usersForceOverride)}
                  disabled={pushUsersLimit.isPending || usersLimit !== tier.default_users_limit}
                  title={
                    usersLimit !== tier.default_users_limit
                      ? 'Save the limit first, then push the saved value.'
                      : usersForceOverride
                        ? 'Push to every tenant on this tier, overwriting ones that customized their default in their own dashboard. Per-license overrides are untouched.'
                        : 'Push to tenants on this tier still on the value the CP last sent them. Tenants that customized their default are skipped; offline tenants come back as failures.'
                  }
                >
                  {pushUsersLimit.isPending ? 'Pushing…' : usersForceOverride ? 'Force-push to all tenants' : 'Push to tenants'}
                </Button>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={usersForceOverride}
                    onChange={(e) => setUsersForceOverride(e.target.checked)}
                    className="cursor-pointer"
                  />
                  Also overwrite customized tenants
                </label>
              </div>
              {usersPushResult && (
                <div className="mt-2 text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2">
                  Pushed to {usersPushResult.ok} tenant{usersPushResult.ok === 1 ? '' : 's'}
                  {usersPushResult.skipped.length > 0 && <>, skipped {usersPushResult.skipped.length} customized</>}
                  {usersPushResult.failed > 0 && <span className="text-red-600">, {usersPushResult.failed} failed</span>}.
                </div>
              )}
            </div>

            <div>
              <Label>Features</Label>
              <div className="mt-1.5 space-y-1.5">
                {features.length === 0 && (
                  <p className="text-xs text-slate-500">No gated features defined.</p>
                )}
                {features.map((f) => (
                  <label
                    key={f.key}
                    className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 cursor-pointer"
                      checked={draft[f.key] === true}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.checked })}
                    />
                    <span>
                      <span className="font-medium">{f.label}</span>{' '}
                      <code className="font-mono text-xs text-slate-500">{f.key}</code>
                      <span className="block text-xs text-slate-500">{f.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor={`credit-mode-${tier.id}`}>Usage allowance</Label>
              <select
                id={`credit-mode-${tier.id}`}
                className="mt-1.5 block w-full max-w-[260px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                value={creditMode}
                onChange={(e) => setCreditMode(e.target.value as CreditMode)}
              >
                {CREDIT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                <span className="font-medium">No usage limits</span> — chats on this tier are free
                and no balance is touched.{' '}
                <span className="font-medium">Dollar credit</span> — every turn deducts its API
                cost.{' '}
                <span className="font-medium">Runtime credit</span> — every turn deducts its
                measured working time in minutes. Either way chat is blocked once the balance
                reaches zero, and the customer sees the same meter: a percentage, a task count and
                a reset date, never a unit.
              </p>

              {unitChanged(tier.credit_mode, creditMode) && (
                <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <span className="font-medium">The unit changes.</span> {allowance} will be
                  pushed as <span className="font-medium">{allowance} {amountUnit(creditMode)}</span>,
                  not {allowance} {amountUnit(tier.credit_mode)}. Saving re-grants every tenant on
                  this tier immediately — set the amount for the new unit before you save.
                </p>
              )}

              {creditMode !== 'none' && (
                <div className="mt-2.5 grid gap-3 sm:grid-cols-2 sm:max-w-[460px]">
                  <div>
                    <Label htmlFor={`allowance-${tier.id}`}>{amountLabel(creditMode)}</Label>
                    <Input
                      id={`allowance-${tier.id}`}
                      type="number"
                      min={0}
                      step={amountStep(creditMode)}
                      value={allowance}
                      onChange={(e) => setAllowance(parseAmount(e.target.value, creditMode))}
                    />
                  </div>
                  <PeriodPicker
                    idPrefix={tier.id}
                    hours={periodHours}
                    custom={periodCustom}
                    onChange={({ hours, custom }) => { setPeriodHours(hours); setPeriodCustom(custom); }}
                  />
                  <div>
                    <Label htmlFor={`tasks-${tier.id}`}>Advertised as (tasks)</Label>
                    <Input
                      id={`tasks-${tier.id}`}
                      type="number"
                      min={0}
                      step={1}
                      value={allowanceTasks}
                      onChange={(e) => setAllowanceTasks(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    />
                  </div>
                </div>
              )}

              {creditMode !== 'none' && (
                <FirstConnectCheckbox
                  checked={resetOnFirstConnect}
                  onChange={setResetOnFirstConnect}
                />
              )}

              {creditMode !== 'none' && (
                <p className="mt-2 text-xs text-slate-500">
                  <span className="font-medium">Advertised as</span> is display only — how many
                  &ldquo;tasks&rdquo; you tell customers this buys. The plugin then counts down in
                  tasks instead of a percentage. Measure a realistic number with{' '}
                  <code className="font-mono text-[11px]">orchestrator/scripts/analyze-task-cost.js</code>{' '}
                  (pass <code className="font-mono text-[11px]">--meter runtime</code> for a runtime
                  tier); 0 leaves the plugin showing a percentage. Nothing is enforced on it.
                  <br />
                  The balance <span className="font-medium">resets</span> to this amount each period
                  — unused allowance does not roll over, and an overdraft is forgiven. 0 means no
                  allowance is granted at all (the balance is whatever an operator tops up). Saving
                  pushes to every tenant on this tier immediately, so an upgrade takes effect at
                  once. Switching the mode re-grants on the new unit right away; the old unit's
                  balance is left where it is, simply no longer spent.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor={`blocked-${tier.id}`}>Chat blocked message</Label>
              <Textarea
                id={`blocked-${tier.id}`}
                rows={3}
                maxLength={2000}
                value={blockedMessage}
                onChange={(e) => setBlockedMessage(e.target.value)}
                placeholder="Your free trial has ended — [upgrade your plan](https://wpgodmode.com/dashboard/) to keep chatting."
              />
              <p className="mt-1 text-xs text-slate-500">
                Non-empty <span className="font-medium">blocks chat</span> for every tenant on this
                tier: sends get this text back as the reply instead of running the agent (works on
                every shipped plugin version), and scheduled runs are refused. Markdown — links open
                in a new tab. Saving a change pushes it to the tier's tenants immediately; moving a
                tenant to a tier without a message unblocks it. Leave empty to allow chat.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteOpen(true)}
                disabled={tier.is_default || allTiers.length <= 1}
                title={
                  tier.is_default
                    ? 'The default tier cannot be deleted — make another tier the default first.'
                    : allTiers.length <= 1
                      ? 'The last remaining tier cannot be deleted.'
                      : undefined
                }
                className="text-red-600 hover:bg-red-50"
              >
                Delete
              </Button>
              {repush && (
                <span className="text-xs text-slate-500">
                  Pushed to {repush.pushed} tenant{repush.pushed === 1 ? '' : 's'}
                  {repush.failed > 0 && <span className="text-red-600"> ({repush.failed} failed)</span>}
                </span>
              )}
            </div>
          </CardBody>
        )}

        <DeleteTierDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          tier={tier}
          otherTiers={allTiers.filter((t) => t.id !== tier.id)}
        />

        <TierTenantsDialog open={tenantsOpen} onOpenChange={setTenantsOpen} tier={tier} />
      </Card>
    </div>
  );
}

function DeleteTierDialog({
  open,
  onOpenChange,
  tier,
  otherTiers,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tier: Tier;
  otherTiers: Tier[];
}) {
  const qc = useQueryClient();
  const needsReassign = tier.tenant_count > 0;
  const [reassignTo, setReassignTo] = useState(otherTiers[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReassignTo(otherTiers[0]?.id ?? '');
    setError(null);
  }, [open, otherTiers]);

  const del = useMutation({
    mutationFn: () => {
      const qs = needsReassign ? `?reassign_to=${encodeURIComponent(reassignTo)}` : '';
      return api.del(`/api/tiers/${encodeURIComponent(tier.id)}${qs}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tiers'] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Delete tier "${tier.label}"`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => del.mutate()}
            disabled={del.isPending || (needsReassign && !reassignTo)}
            className="bg-red-600 hover:bg-red-700"
          >
            {del.isPending ? 'Deleting…' : 'Delete tier'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        {needsReassign ? (
          <>
            <p className="text-sm text-slate-700">
              <b>{tier.tenant_count}</b> tenant{tier.tenant_count === 1 ? ' is' : 's are'} on this
              tier. Move {tier.tenant_count === 1 ? 'it' : 'them'} to another tier before deleting —
              their feature entitlements will be re-pushed to match the new tier.
            </p>
            <div>
              <Label htmlFor="reassign_to">Move tenants to</Label>
              <Select
                id="reassign_to"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
              >
                {otherTiers.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label} ({t.id})
                  </option>
                ))}
              </Select>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-700">
            No tenants are on this tier. This permanently removes it.
          </p>
        )}
      </div>
    </Dialog>
  );
}

// Lists the tenants currently on a tier, each linking to its detail page.
// Fetches on open via the tier filter on GET /api/tenants; the ['tenants', …]
// key shares the tenants-list cache namespace, so a tier reassignment (which
// invalidates ['tenants']) refreshes this list too.
function TierTenantsDialog({
  open,
  onOpenChange,
  tier,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  tier: Tier;
}) {
  const tenants = useQuery({
    queryKey: ['tenants', { tier: tier.id }],
    queryFn: () =>
      api.get<{ tenants: Tenant[] }>(`/api/tenants?tier=${encodeURIComponent(tier.id)}`),
    enabled: open,
  });
  const list = tenants.data?.tenants ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={
        <>
          Tenants on <span className="font-mono text-slate-500">{tier.id}</span>
        </>
      }
    >
      {tenants.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : tenants.isError ? (
        <div className="text-sm text-red-600">Failed to load tenants.</div>
      ) : list.length === 0 ? (
        <div className="text-sm text-slate-500">No tenants on this tier.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((t) => (
            <li key={t.id}>
              <Link
                to={`/tenants/${t.id}`}
                onClick={() => onOpenChange(false)}
                className="-mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 hover:bg-slate-50"
              >
                <span className="min-w-0">
                  <span className="font-mono text-xs text-slate-900">{t.id}</span>
                  {t.customer_email && (
                    <span className="block truncate text-xs text-slate-500">
                      {t.customer_email}
                    </span>
                  )}
                </span>
                <Badge tone={statusTone(t.status)}>{t.status}</Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function NewTierDialog({
  open,
  onOpenChange,
  features,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  features: FeatureDef[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ id: '', label: '', description: '' });
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [limit, setLimit] = useState(0);
  const [usersLimit, setUsersLimit] = useState(1);
  const [blockedMessage, setBlockedMessage] = useState('');
  const [creditMode, setCreditMode] = useState<CreditMode>('none');
  const [allowance, setAllowance] = useState(0);
  const [periodHours, setPeriodHours] = useState(0);
  const [periodCustom, setPeriodCustom] = useState(false);
  const [resetOnFirstConnect, setResetOnFirstConnect] = useState(false);
  const [allowanceTasks, setAllowanceTasks] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ id: '', label: '', description: '' });
      setDraft({});
      setLimit(0);
      setUsersLimit(1);
      setBlockedMessage('');
      setCreditMode('none');
      setAllowance(0);
      setPeriodHours(0);
      setPeriodCustom(false);
      setResetOnFirstConnect(false);
      setAllowanceTasks(0);
      setError(null);
    }
  }, [open]);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ tier: Tier }>('/api/tiers', {
        id: form.id,
        label: form.label,
        description: form.description || null,
        default_activations_limit: limit,
        default_users_limit: usersLimit,
        chat_blocked_message: blockedMessage,
        credit_mode: creditMode,
        credit_allowance: allowance,
        credit_period_hours: periodHours,
        credit_reset_on_first_connect: resetOnFirstConnect,
        allowance_tasks: allowanceTasks,
        features: draft,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tiers'] });
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New tier"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !form.id || !form.label}>
            {create.isPending ? 'Creating…' : 'Create tier'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="tier-id">Tier ID (slug)</Label>
            <Input
              id="tier-id"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              placeholder="agency"
            />
            <p className="text-xs text-slate-500 mt-1">Lowercase + hyphens. Can be renamed later.</p>
          </div>
          <div>
            <Label htmlFor="tier-label">Label</Label>
            <Input
              id="tier-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Agency"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="tier-desc">Description</Label>
          <Textarea
            id="tier-desc"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="tier-limit">Default activation limit</Label>
          <Input
            id="tier-limit"
            type="number"
            min={0}
            value={limit}
            onChange={(e) => setLimit(Math.max(0, parseInt(e.target.value, 10) || 0))}
            placeholder="0"
          />
          <p className="text-xs text-slate-500 mt-1">
            Seed for license keys' activation cap on this tier (0 blocks all activations). Applied at tenant
            creation.
          </p>
        </div>
        <div>
          <Label htmlFor="tier-users-limit">Default user limit</Label>
          <Input
            id="tier-users-limit"
            type="number"
            min={1}
            value={usersLimit}
            onChange={(e) => setUsersLimit(Math.max(1, parseInt(e.target.value, 10) || 1))}
            placeholder="1"
          />
          <p className="text-xs text-slate-500 mt-1">
            Seed for license keys' user (seat) cap on this tier (minimum 1 — a site always has its
            owner). Applied at tenant creation.
          </p>
        </div>
        <div>
          <Label htmlFor="tier-credit-mode">Usage allowance</Label>
          <select
            id="tier-credit-mode"
            className="mt-1.5 block w-full max-w-[260px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            value={creditMode}
            onChange={(e) => setCreditMode(e.target.value as CreditMode)}
          >
            {CREDIT_MODES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          {creditMode !== 'none' && (
            <div className="mt-2.5 grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="tier-allowance">{amountLabel(creditMode)}</Label>
                <Input
                  id="tier-allowance"
                  type="number"
                  min={0}
                  step={amountStep(creditMode)}
                  value={allowance}
                  onChange={(e) => setAllowance(parseAmount(e.target.value, creditMode))}
                />
              </div>
              <PeriodPicker
                idPrefix="new-tier"
                hours={periodHours}
                custom={periodCustom}
                onChange={({ hours, custom }) => { setPeriodHours(hours); setPeriodCustom(custom); }}
              />
              <div>
                <Label htmlFor="tier-tasks">Advertised as (tasks)</Label>
                <Input id="tier-tasks" type="number" min={0} step={1} value={allowanceTasks}
                  onChange={(e) => setAllowanceTasks(Math.max(0, parseInt(e.target.value, 10) || 0))} />
              </div>
            </div>
          )}
          {creditMode !== 'none' && (
            <FirstConnectCheckbox checked={resetOnFirstConnect} onChange={setResetOnFirstConnect} />
          )}
          <p className="text-xs text-slate-500 mt-1">
            Dollar credit meters each turn's API cost; runtime credit meters its measured working
            time in minutes. The balance resets to the amount above each period — no rollover. 0
            grants nothing.
          </p>
        </div>
        <div>
          <Label htmlFor="tier-blocked">Chat blocked message</Label>
          <Textarea
            id="tier-blocked"
            rows={3}
            maxLength={2000}
            value={blockedMessage}
            onChange={(e) => setBlockedMessage(e.target.value)}
          />
          <p className="text-xs text-slate-500 mt-1">
            Non-empty blocks chat for tenants on this tier — sends get this text (Markdown) back
            instead of an agent reply. Leave empty to allow chat.
          </p>
        </div>
        <div>
          <Label>Features</Label>
          <div className="mt-1.5 space-y-1.5">
            {features.length === 0 && (
              <p className="text-xs text-slate-500">No gated features defined.</p>
            )}
            {features.map((f) => (
              <label
                key={f.key}
                className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 cursor-pointer"
                  checked={draft[f.key] === true}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.checked })}
                />
                <span>
                  <span className="font-medium">{f.label}</span>{' '}
                  <code className="font-mono text-xs text-slate-500">{f.key}</code>
                  <span className="block text-xs text-slate-500">{f.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// Shared by the tier card and the new-tier dialog so the two can't describe
// the option differently.
function FirstConnectCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="mt-2.5 flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
      <input
        type="checkbox"
        className="mt-0.5 cursor-pointer"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="font-medium">Restart when the first AI provider is connected</span>
        <span className="block text-xs text-slate-500">
          Once per tenant. When the customer connects an AI provider for the first time (a
          subscription or an API key), the balance resets to the full amount and the period starts
          again from that moment — the days between signup and connecting don't use up the first
          period. Reconnecting later restarts nothing, and tenants that already had a provider
          connected are left alone.
        </span>
      </span>
    </label>
  );
}

// Compare two feature maps by their enabled keys.
// pg hands NUMERIC back as a string, so every read of credit_allowance has to
// coerce before it's compared or rendered.
function allowanceNum(v: number | string): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Friendly cadence picker over the raw hours column. "Custom" is the escape
// hatch (and what makes a 1-hour period testable without a migration).
const PERIOD_PRESETS = [
  { value: 0, label: 'One-time (never renews)' },
  { value: 24, label: 'Daily' },
  { value: 168, label: 'Weekly' },
  { value: 720, label: 'Monthly (30 days)' },
];

// Whether a stored hours value needs the custom input to represent it. Used to
// seed `custom` from the server row; NOT used to derive it while editing — see
// PeriodPicker.
export function isCustomPeriod(hours: number): boolean {
  return !PERIOD_PRESETS.some((p) => p.value === hours);
}

/**
 * Reset-cadence picker: a preset dropdown plus an "Every N hours" box that
 * appears when Custom is chosen.
 *
 * `custom` is explicit state, deliberately NOT derived from `hours`. Deriving it
 * meant picking "Custom…" changed nothing (the hours were still a preset, so the
 * select snapped back and the input never appeared) — you could not configure a
 * custom cadence at all. It also means typing 24 into the custom box doesn't
 * yank the control back to "Daily" mid-edit.
 *
 * Shared by the tier card and the new-tier dialog so the two can't drift.
 */
function PeriodPicker({
  idPrefix,
  hours,
  custom,
  onChange,
}: {
  idPrefix: string;
  hours: number;
  custom: boolean;
  onChange: (next: { hours: number; custom: boolean }) => void;
}) {
  return (
    <>
      <div>
        <Label htmlFor={`period-${idPrefix}`}>Resets</Label>
        <select
          id={`period-${idPrefix}`}
          className="border-input h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          value={custom ? 'custom' : String(hours)}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              // Keep the current hours as the starting point for editing.
              onChange({ hours, custom: true });
              return;
            }
            onChange({ hours: Number(e.target.value), custom: false });
          }}
        >
          {PERIOD_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
          <option value="custom">Custom…</option>
        </select>
      </div>
      {custom && (
        <div>
          <Label htmlFor={`period-hours-${idPrefix}`}>Every N hours</Label>
          <Input
            id={`period-hours-${idPrefix}`}
            type="number"
            min={0}
            step={1}
            value={hours}
            onChange={(e) => onChange({ hours: Math.max(0, parseInt(e.target.value, 10) || 0), custom: true })}
          />
        </div>
      )}
    </>
  );
}

function sameFeatures(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const ka = Object.keys(a)
    .filter((k) => a[k])
    .sort();
  const kb = Object.keys(b)
    .filter((k) => b[k])
    .sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i]);
}
